// @TODO remove the whole file after testing indexed db implementation
// commented bacauset the loader is not hiding since members store is empty
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  difference,
  fromPairs,
  intersection,
  keyBy,
  union,
  uniq,
} from 'lodash';
import { Company, CompanyId, ProjectId, User, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import {
  ProjectUsersByAccess,
  UsersMetadata,
} from 'components/organisms/tasks/fields';
import { Content } from 'stores/core';
import { MembershipsStore } from 'stores/memberships';
import { Project, ProjectsStore } from 'stores/projects';
import { UsersStore } from 'stores/users';
import { memoForCallbacks } from 'utils/memoForCallbacks';

export const constructUserMeta = memoForCallbacks(
  ({
    membershipsStore,
    usersStore,
    projectsStore,
    native_user_ids,
    current_company_user_ids,
    active_user_ids,
    current_company_active_user_ids,
    projectIds,
    users_list,
    currentUser,
  }: {
    membershipsStore: MembershipsStore;
    usersStore: UsersStore;
    projectsStore: ProjectsStore;
    current_company_user_ids: number[];
    native_user_ids: number[];
    active_user_ids: number[];
    current_company_active_user_ids: number[];
    projectIds: ProjectId[];
    users_list: User[];
    currentUser: User;
  }): UsersMetadata => {
    const getTupleById = (projectId) => {
      const [_, _projectId] = projectId;
      const memberships =
        MembershipsStore.ofProject(projectId)(membershipsStore);
      const project_member_ids = memberships[1]?.users ?? [];
      const project = ProjectsStore.get(projectId)(projectsStore);
      const project_customer_company_user_ids = Content.orElse<number[]>([])(
        Content.map(({ customer }: Project) =>
          UsersStore.ofCompanyId(CompanyId(customer))(usersStore),
        )(project),
      );
      const {
        userType: current_user_type,
        company: { companyId: current_user_company_id },
      } = currentUser;
      const partners_in_project = Content.orElse<Company[]>([])(
        Content.map(({ partners }: Project) => partners)(project),
      );
      const permissive_partners = (partners_in_project ?? []).filter(
        (x) =>
          x.accessLevel?.partnerPermissionSetName ===
          PartnerPermissionName.Permissive,
      );
      const restricted_moderate_partners = (partners_in_project ?? []).filter(
        (x) =>
          x.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Moderate ||
          x.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
      );
      const private_users_of_partners_in_project = (
        permissive_partners ?? []
      ).reduce(
        (result, partner) =>
          result.concat(
            UsersStore.ofCompanyId(CompanyId(partner.companyId))(usersStore),
          ),
        [] as number[],
      );
      const shared_users_of_partners_in_project = (
        restricted_moderate_partners ?? []
      ).reduce(
        (result, partner) =>
          result.concat(
            UsersStore.ofCompanyId(CompanyId(partner.companyId))(usersStore),
          ),
        [] as number[],
      );
      const is_current_user_company_associated_with_project =
        current_user_type === UserType.Partner
          ? !!(partners_in_project ?? []).find(
              ({ companyId }) => companyId === current_user_company_id,
            )
          : false;
      const private_user_ids = uniq(
        union(
          current_user_type === UserType.Partner &&
            !is_current_user_company_associated_with_project
            ? []
            : intersection(
                current_company_user_ids,
                private_users_of_partners_in_project,
              ),
          native_user_ids,
          private_users_of_partners_in_project,
        ),
      );
      const private_active_user_ids = intersection(
        active_user_ids,
        private_user_ids,
      );
      const shared_user_ids = uniq(
        union(
          private_user_ids,
          shared_users_of_partners_in_project,
          project_customer_company_user_ids,
        ),
      );
      const shared_active_user_ids = intersection(
        active_user_ids,
        shared_user_ids,
      );
      const inviteable_shared_user_ids =
        current_user_type === UserType.Partner
          ? is_current_user_company_associated_with_project
            ? current_company_active_user_ids
            : []
          : current_user_type === UserType.External
          ? current_company_active_user_ids
          : difference(
              shared_active_user_ids,
              union(
                private_users_of_partners_in_project,
                shared_users_of_partners_in_project,
              ),
            );

      const project_users: ProjectUsersByAccess = {
        private: {
          in_project_user_ids: intersection(
            project_member_ids,
            private_active_user_ids,
          ),
          not_in_project_user_ids: difference(
            current_user_type === UserType.Partner &&
              !is_current_user_company_associated_with_project
              ? []
              : intersection(
                  current_company_active_user_ids,
                  private_active_user_ids,
                ),
            project_member_ids,
          ),
        },
        shared: {
          in_project_user_ids: intersection(
            project_member_ids,
            shared_active_user_ids,
          ),
          not_in_project_user_ids: difference(
            inviteable_shared_user_ids,
            project_member_ids,
          ),
        },
      };

      return [projectId[1], project_users, shared_active_user_ids] as [
        number,
        ProjectUsersByAccess,
        number[],
      ];
    };

    const tuples = projectIds.map(getTupleById);
    const by_user_id = keyBy(users_list, 'userId');
    const users = {
      active_user_ids: tuples.reduce(
        (all_active, [, , shared_active]) => union(all_active, shared_active),
        [] as number[],
      ),
      by_user_id,
      by_project_id: fromPairs(tuples),
    } as UsersMetadata;

    return users;
  },
  (old, cur) => {
    if (!old) {
      return false;
    }
    if (!cur) {
      return false;
    }
    let areEqual = true;

    // note to future refactors.
    // Here if conditions can't be swapped because all user count change is directly related to active user count. SO swapping condition would likely to show an stale data
    if (old.projectIds?.length !== cur.projectIds?.length) {
      areEqual = false;
    }
    if (old.active_user_ids?.length !== cur.active_user_ids?.length) {
      areEqual = false;
    }
    if (old.native_user_ids?.length !== cur.native_user_ids?.length) {
      areEqual = false;
    }
    if (
      old.current_company_active_user_ids?.length !==
      cur.current_company_active_user_ids?.length
    ) {
      areEqual = false;
    }
    if (
      old.current_company_user_ids?.length !==
      cur.current_company_user_ids?.length
    ) {
      areEqual = false;
    }
    if (old.membershipsStore !== cur.membershipsStore) {
      areEqual = false;
    }
    if (old.usersStore !== cur.usersStore) {
      areEqual = false;
    }
    if (areEqual) {
      return true;
    }

    return false;
  },
);
